import { getMonthlyBillAmount } from '@fruitful/fruitils';
import type { Dollars } from '@fruitful/types';
import { useEffect, useRef, useState } from 'react';
import * as React from 'react';
import {
    KeyboardAvoidingView,
    Platform,
    type TextInput,
    TouchableWithoutFeedback,
} from 'react-native';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    H1Emphasized,
    isNonMobileWeb,
    StyledH1EmphasizedInput,
    XStack,
    YStack,
} from 'smoothie';

import {
    formatCurrencyInputOnChange,
    formatDollars,
} from '../../../common/utils/bankingUtils';
import type { PartialBill } from '../../types/Bill';

const AmountLabel = () => {
    return <BodyMediumRegular>Exact or approximate amount</BodyMediumRegular>;
};

export const EditableAmountField = ({
    bill,
    onPress,
}: {
    bill?: PartialBill;
    onPress: () => void;
}) => {
    return (
        <TouchableWithoutFeedback onPress={onPress}>
            <YStack alignItems="center" justifyContent="center">
                <H1Emphasized>{formatDollars(bill?.amount ?? 0)}</H1Emphasized>
                <AmountLabel />
            </YStack>
        </TouchableWithoutFeedback>
    );
};

const AmountInput = ({
    amount,
    onChange,
}: {
    amount: string | undefined;
    onChange: (amountString: string) => void;
}) => {
    const inputRef = useRef<TextInput>(null);
    const billAmount = amount?.toString() ?? '';

    useEffect(() => {
        setTimeout(() => {
            inputRef.current?.focus();
        }, 100);
    }, []);

    return (
        <YStack alignItems="center" justifyContent="center">
            <XStack>
                <StyledH1EmphasizedInput
                    ref={inputRef}
                    placeholder="0"
                    autoComplete="off"
                    autoCorrect={false}
                    inputMode="decimal"
                    keyboardType="decimal-pad"
                    value={billAmount}
                    onChangeText={(text: string) => {
                        onChange(formatCurrencyInputOnChange(billAmount, text));
                    }}
                    paddingRight={10}
                    paddingLeft={10}
                    borderWidth={0}
                    textAlign="center"
                    width="auto"
                    height="auto"
                />
            </XStack>
            <AmountLabel />
        </YStack>
    );
};

interface AmountEditorProps {
    bill: PartialBill;
    onSubmit: (updatedBill: PartialBill) => void;
}

const AmountEditor = ({ bill, onSubmit }: AmountEditorProps) => {
    const [amountInputValue, setAmountInputValue] = useState<
        string | undefined
    >(bill.amount !== undefined ? bill.amount.toString() : undefined);

    return (
        <KeyboardAvoidingView
            style={{
                flex: 1,
                height: '100%',
            }}
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            keyboardVerticalOffset={70}
        >
            <YStack
                height="100%"
                justifyContent={isNonMobileWeb ? 'flex-start' : 'space-between'}
                alignItems="center"
            >
                <YStack
                    marginVertical={100}
                    justifyContent="center"
                    alignItems="center"
                >
                    <AmountInput
                        amount={amountInputValue}
                        onChange={setAmountInputValue}
                    />
                </YStack>
                <Button
                    marginTop={12}
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    text="Confirm"
                    onPress={() => {
                        let amount: Dollars | undefined = parseFloat(
                            amountInputValue?.replace(/[^0-9.]/g, '') ?? '',
                        );
                        if (isNaN(amount)) {
                            amount = undefined;
                        }

                        const monthlyAmount = getMonthlyBillAmount(
                            amount,
                            bill.cadence,
                        );
                        // TODO handle validation, error
                        onSubmit({
                            ...bill,
                            amount,
                            monthlyAmount,
                        });
                    }}
                />
            </YStack>
        </KeyboardAvoidingView>
    );
};

export default AmountEditor;
